// PROMPT 16-ACCESS-CONTROL-MATRIX-UI-R3.2B-FOLLOW-UP (Full E)
// Tri-State popover cell untuk mengatur permission per aksi (Ikuti Default / Izinkan / Tolak).
// Non-modal popover — TIDAK trap fokus permanen; user tetap bisa Tab keluar.
// Keyboard: Enter/Space membuka, ArrowUp/Down memilih, Enter mengonfirmasi, Escape/klik luar menutup.
// Fokus kembali ke trigger setelah popover ditutup.
import { useRef, useState, useEffect } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Check, X, Minus, ChevronDown } from "lucide-react";

const STATE_META = {
  default: { label: "Ikuti Default", short: "—", cls: "bg-slate-100 border-slate-400 text-slate-500", wideCls: "bg-white border-slate-300 text-slate-700", icon: Minus, ariaState: "mengikuti default" },
  allow:   { label: "Izinkan",       short: "✓", cls: "bg-emerald-600 border-emerald-700 text-white", wideCls: "bg-emerald-50 border-emerald-500 text-emerald-800", icon: Check, ariaState: "diizinkan khusus" },
  deny:    { label: "Tolak",         short: "✕", cls: "bg-rose-600 border-rose-700 text-white",       wideCls: "bg-rose-50 border-rose-500 text-rose-800",       icon: X,     ariaState: "ditolak khusus" },
};

export default function TriStatePermCell({
  feature,
  permKey,
  permLabel,
  state,          // "default" | "allow" | "deny"
  inheritedOn,    // boolean — nilai default matrix (div×level)
  effectiveOn,    // boolean — hasil setelah tri-state calc
  disabled = false,
  sensitive = false,
  onChange,       // (newState) => void
  testid,
  wide = false,   // R3.2D.2: render as wide Select-like trigger showing state label + chevron
}) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef(null);
  const optionsRef = useRef([]);
  const meta = STATE_META[state] || STATE_META.default;
  const Icon = meta.icon;

  const options = [
    { value: "default", label: "Ikuti Default", desc: `Default: ${inheritedOn ? "Aktif" : "Tidak Aktif"}`, cls: "bg-slate-700 hover:bg-slate-800 text-white border-slate-800" },
    { value: "allow",   label: "Izinkan",       desc: "Set akses ini sebagai Diizinkan Khusus", cls: "bg-emerald-600 hover:bg-emerald-700 text-white border-emerald-700" },
    { value: "deny",    label: "Tolak",         desc: "Set akses ini sebagai Ditolak Khusus",   cls: "bg-rose-600 hover:bg-rose-700 text-white border-rose-700" },
  ];

  const currentIndex = Math.max(0, options.findIndex((o) => o.value === state));

  // Auto-focus opsi terpilih saat popover terbuka
  useEffect(() => {
    if (open) {
      // beri Radix waktu untuk mount konten dulu
      const t = setTimeout(() => {
        const el = optionsRef.current[currentIndex] || optionsRef.current[0];
        el?.focus();
      }, 15);
      return () => clearTimeout(t);
    }
  }, [open, currentIndex]);

  const closeAndReturnFocus = () => {
    setOpen(false);
    // Radix will handle focus return via onOpenAutoFocus / onCloseAutoFocus, tapi kita fallback manual.
    setTimeout(() => triggerRef.current?.focus(), 20);
  };

  const handleSelect = (value) => {
    onChange?.(value);
    closeAndReturnFocus();
  };

  const handleTriggerKeyDown = (e) => {
    if (disabled) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      setOpen((v) => !v);
    }
  };

  const handleOptionKeyDown = (e, idx) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      const next = (idx + 1) % options.length;
      optionsRef.current[next]?.focus();
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      const prev = (idx - 1 + options.length) % options.length;
      optionsRef.current[prev]?.focus();
    } else if (e.key === "Escape") {
      e.preventDefault();
      closeAndReturnFocus();
    } else if (e.key === "Home") {
      e.preventDefault();
      optionsRef.current[0]?.focus();
    } else if (e.key === "End") {
      e.preventDefault();
      optionsRef.current[options.length - 1]?.focus();
    }
    // Tab / Shift+Tab: biarkan browser handle → popover tetap terbuka? Kita tutup untuk cegah bingung.
    else if (e.key === "Tab") {
      // Non-modal: biarkan fokus keluar. Radix akan mempertahankan open state.
      // Namun untuk UX konsisten, tutup popover saat fokus keluar dari options.
      setTimeout(() => {
        const active = document.activeElement;
        const stillInside = optionsRef.current.some((r) => r === active);
        if (!stillInside) setOpen(false);
      }, 0);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen} modal={false}>
      <PopoverTrigger asChild>
        {wide ? (
          <button
            ref={triggerRef}
            type="button"
            disabled={disabled}
            data-testid={testid || `tristate-cell-${feature}-${permKey}`}
            data-state={state}
            aria-haspopup="menu"
            aria-expanded={open}
            aria-label={`${permLabel}: ${meta.ariaState}${sensitive ? " (sensitif)" : ""}. Tekan Enter untuk mengubah`}
            title={`${permLabel} — ${meta.label}${sensitive ? " · Sensitif" : ""}`}
            onKeyDown={handleTriggerKeyDown}
            className={`relative w-full min-w-[140px] h-8 pl-2 pr-6 border inline-flex items-center gap-1.5 text-[11px] font-semibold rounded-none transition-all outline-none focus-visible:ring-2 focus-visible:ring-[#FF5A1F] ${meta.wideCls} ${disabled ? "opacity-40 cursor-not-allowed" : "cursor-pointer hover:brightness-95"}`}
          >
            <span className={`w-4 h-4 inline-flex items-center justify-center border ${state === "allow" ? "bg-emerald-600 border-emerald-700 text-white" : state === "deny" ? "bg-rose-600 border-rose-700 text-white" : "bg-slate-200 border-slate-300 text-slate-500"}`} aria-hidden="true">
              <Icon size={10} strokeWidth={3} />
            </span>
            <span className="flex-1 text-left truncate">{meta.label}</span>
            <ChevronDown size={12} className="absolute right-1.5 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" aria-hidden="true" />
            {sensitive && (
              <span
                aria-hidden="true"
                className="absolute -top-1 -right-1 w-2 h-2 bg-amber-500 border border-amber-700 rounded-full"
                title="Permission sensitif"
              />
            )}
            <span className="sr-only">
              {permLabel} — {meta.ariaState}. Nilai efektif: {effectiveOn ? "Aktif" : "Tidak Aktif"}.
            </span>
          </button>
        ) : (
          <button
            ref={triggerRef}
            type="button"
            disabled={disabled}
            data-testid={testid || `tristate-cell-${feature}-${permKey}`}
            data-state={state}
            aria-haspopup="menu"
            aria-expanded={open}
            aria-label={`${permLabel}: ${meta.ariaState}${sensitive ? " (sensitif)" : ""}. Tekan Enter untuk mengubah`}
            title={`${permLabel} — ${meta.label}${sensitive ? " · Sensitif" : ""}`}
            onKeyDown={handleTriggerKeyDown}
            className={`relative w-7 h-7 border-2 inline-flex items-center justify-center transition-all outline-none focus-visible:ring-2 focus-visible:ring-[#FF5A1F] focus-visible:ring-offset-1 ${meta.cls} ${disabled ? "opacity-40 cursor-not-allowed" : "cursor-pointer hover:scale-105"}`}
          >
            <Icon size={14} strokeWidth={state === "default" ? 2 : 3} aria-hidden="true" />
            {sensitive && (
              <span
                aria-hidden="true"
                className="absolute -top-1 -right-1 w-2 h-2 bg-amber-500 border border-amber-700 rounded-full"
                title="Permission sensitif"
              />
            )}
            <span className="sr-only">
              {permLabel} — {meta.ariaState}. Nilai efektif: {effectiveOn ? "Aktif" : "Tidak Aktif"}.
            </span>
          </button>
        )}
      </PopoverTrigger>
      <PopoverContent
        align="center"
        side="bottom"
        sideOffset={6}
        className="w-64 p-0 rounded-none border-2 border-slate-800 shadow-lg"
        onOpenAutoFocus={(e) => e.preventDefault()}
        onCloseAutoFocus={(e) => {
          e.preventDefault();
          triggerRef.current?.focus();
        }}
        onEscapeKeyDown={() => setOpen(false)}
        onInteractOutside={() => setOpen(false)}
        data-testid={`tristate-popover-${feature}-${permKey}`}
      >
        <div className="px-3 py-2 bg-slate-800 text-white">
          <div className="text-[10px] uppercase tracking-wider font-bold flex items-center gap-1">
            {permLabel}
            {sensitive && (
              <span className="px-1 text-[8px] bg-amber-500 text-amber-950 border border-amber-700" title="Permission sensitif — butuh alasan saat save">Sensitif</span>
            )}
          </div>
          <div className="text-[9px] text-slate-300 font-mono">{feature}.{permKey}</div>
        </div>
        <div className="p-1.5 flex flex-col gap-1" role="menu" aria-label={`Pilih status untuk ${permLabel}`}>
          {options.map((opt, idx) => {
            const isCurrent = opt.value === state;
            return (
              <button
                key={opt.value}
                ref={(el) => (optionsRef.current[idx] = el)}
                type="button"
                role="menuitemradio"
                aria-checked={isCurrent}
                data-testid={`tristate-option-${feature}-${permKey}-${opt.value}`}
                onClick={() => handleSelect(opt.value)}
                onKeyDown={(e) => handleOptionKeyDown(e, idx)}
                className={`w-full text-left px-2.5 py-2 border-2 focus:outline-none focus:ring-2 focus:ring-[#FF5A1F] transition-colors ${
                  isCurrent
                    ? opt.cls + " ring-2 ring-[#FF5A1F] ring-offset-1"
                    : "bg-white text-slate-800 border-slate-300 hover:border-slate-500 hover:bg-slate-50"
                }`}
              >
                <div className="text-[11px] font-bold uppercase tracking-wider flex items-center gap-1.5">
                  {opt.value === "default" && <Minus size={12} strokeWidth={3} aria-hidden="true" />}
                  {opt.value === "allow" && <Check size={12} strokeWidth={3} aria-hidden="true" />}
                  {opt.value === "deny" && <X size={12} strokeWidth={3} aria-hidden="true" />}
                  <span>{opt.label}</span>
                  {isCurrent && <span className="ml-auto text-[8px] bg-white/25 px-1 py-0.5 border border-white/40 uppercase">Aktif</span>}
                </div>
                <div className={`text-[9px] mt-0.5 ${isCurrent ? "text-white/85" : "text-slate-500"}`}>{opt.desc}</div>
              </button>
            );
          })}
        </div>
        <div className="px-3 py-1.5 bg-slate-50 border-t border-slate-200 text-[9px] text-slate-600 flex items-center justify-between">
          <span>
            Efektif: <b className={effectiveOn ? "text-emerald-700" : "text-slate-500"}>{effectiveOn ? "Aktif" : "Tidak Aktif"}</b>
          </span>
          <span className="italic text-slate-500">Esc / klik luar untuk tutup</span>
        </div>
      </PopoverContent>
    </Popover>
  );
}
